import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook optimizado para minimizar el trabajo del hilo principal
 * Implementa estrategias para evitar tareas largas y mejorar el rendimiento
 */
export const useMainThreadOptimization = () => {
  const taskQueue = useRef([]);
  const isProcessing = useRef(false);
  const frameId = useRef(null);

  // Procesar tareas en chunks pequeños para evitar bloquear el hilo principal
  const processTaskQueue = useCallback(() => {
    if (taskQueue.current.length === 0 || isProcessing.current) {
      return;
    }

    isProcessing.current = true;
    const startTime = performance.now();
    const timeSlice = 5; // 5ms por frame para mantener 60fps

    while (taskQueue.current.length > 0 && (performance.now() - startTime) < timeSlice) {
      const task = taskQueue.current.shift();
      try {
        task();
      } catch (error) {
        console.warn('Error en tarea del hilo principal:', error);
      }
    }

    isProcessing.current = false;

    // Si quedan tareas, programar el siguiente chunk
    if (taskQueue.current.length > 0) {
      frameId.current = requestAnimationFrame(processTaskQueue);
    }
  }, []);

  // Programar una tarea para ejecutar de forma no bloqueante
  const scheduleTask = useCallback((task, priority = 'normal') => {
    if (typeof task !== 'function') {
      console.warn('scheduleTask espera una función');
      return;
    }

    // Priorizar tareas críticas al inicio de la cola
    if (priority === 'high') {
      taskQueue.current.unshift(task);
    } else {
      taskQueue.current.push(task);
    }

    // Iniciar procesamiento si no está en curso
    if (!isProcessing.current && !frameId.current) {
      frameId.current = requestAnimationFrame(processTaskQueue);
    }
  }, [processTaskQueue]);

  // Ejecutar operaciones DOM de forma optimizada
  const optimizedDOMOperation = useCallback((operation) => {
    return new Promise((resolve) => {
      scheduleTask(() => {
        requestAnimationFrame(() => {
          try {
            const result = operation();
            resolve(result);
          } catch (error) {
            console.warn('Error en operación DOM:', error);
            resolve(null);
          }
        });
      }, 'high');
    });
  }, [scheduleTask]);

  // Throttle optimizado para eventos frecuentes
  const createOptimizedThrottle = useCallback((callback, delay = 16) => {
    let lastCall = 0;
    let timeoutId = null;

    return (...args) => {
      const now = performance.now();
      
      if (now - lastCall >= delay) {
        lastCall = now;
        scheduleTask(() => callback(...args));
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          lastCall = performance.now();
          scheduleTask(() => callback(...args));
        }, delay - (now - lastCall));
      }
    };
  }, [scheduleTask]);

  // Debounce optimizado
  const createOptimizedDebounce = useCallback((callback, delay = 300) => {
    let timeoutId = null;

    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        scheduleTask(() => callback(...args));
      }, delay);
    };
  }, [scheduleTask]);

  // Ejecutar tareas cuando el navegador esté inactivo
  const scheduleIdleTask = useCallback((task, options = {}) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback((deadline) => {
        if (deadline.timeRemaining() > 0) {
          scheduleTask(task);
        } else {
          // Fallback si no hay tiempo libre
          setTimeout(() => scheduleTask(task), 0);
        }
      }, { timeout: options.timeout || 1000 });
    } else {
      // Fallback para navegadores que no soportan requestIdleCallback
      setTimeout(() => scheduleTask(task), 0);
    }
  }, [scheduleTask]);

  // Batch DOM reads para evitar layout thrashing
  const batchDOMReads = useCallback((reads) => {
    return new Promise((resolve) => {
      scheduleTask(() => {
        requestAnimationFrame(() => {
          const results = reads.map(read => {
            try {
              return read();
            } catch (error) {
              console.warn('Error en lectura DOM:', error);
              return null;
            }
          });
          resolve(results);
        });
      }, 'high');
    });
  }, [scheduleTask]);

  // Batch DOM writes para optimizar reflows
  const batchDOMWrites = useCallback((writes) => {
    return new Promise((resolve) => {
      scheduleTask(() => {
        requestAnimationFrame(() => {
          writes.forEach(write => {
            try {
              write();
            } catch (error) {
              console.warn('Error en escritura DOM:', error);
            }
          });
          resolve();
        });
      }, 'high');
    });
  }, [scheduleTask]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (frameId.current) {
        cancelAnimationFrame(frameId.current);
      }
      taskQueue.current = [];
      isProcessing.current = false;
    };
  }, []);

  // Monitorear rendimiento
  const measurePerformance = useCallback((name, task) => {
    return (...args) => {
      const start = performance.now();
      const result = task(...args);
      const end = performance.now();
      
      // Solo log si la tarea toma más de 16ms (puede bloquear el frame)
      if (end - start > 16) {
        console.warn(`Tarea larga detectada: ${name} - ${(end - start).toFixed(2)}ms`);
      }
      
      return result;
    };
  }, []);

  return {
    scheduleTask,
    optimizedDOMOperation,
    createOptimizedThrottle,
    createOptimizedDebounce,
    scheduleIdleTask,
    batchDOMReads,
    batchDOMWrites,
    measurePerformance,
    getQueueLength: () => taskQueue.current.length
  };
};

export default useMainThreadOptimization;